import type { ReactNode } from 'react';
import React, { Suspense } from 'react';

import { Navigate } from 'react-router';

import { pipe } from '@code-expert/prelude';
import type { CourseOption } from '/imports/api/rpc/courseSelect/shared';
import type { CourseUrl, SemesterId, UserPublic } from '/imports/domain';
import { concatRoutes, formatRoute, parser } from '/imports/modules/Routing';
import type { AuthoriseProps } from '/imports/ui/components/Authorise';
import { Authorise } from '/imports/ui/components/Authorise';
import { Loading } from '/imports/ui/components/Loading';
import NotFound from '/imports/ui/components/NotFound';
import { useSetPreferredEntry } from '/imports/ui/hooks/useSetPreferredEntry';
import { TopNavLayout } from '/imports/ui/layouts/TopNavLayout';
import { setPageParamsAdt } from '/imports/ui/navigation/pageParams';
import { setPageParamsUiState } from '/imports/ui/navigation/pageParamsUiState';
import type { Page } from './pages';
import {
  enroledAnalyticsRoute,
  enroledCodeExamplesRoute,
  enroledCourseRoute,
  enroledExercisesRoute,
  enroledGroupsRoute,
  enroledRoute,
} from './routes';

const Navigation = React.lazy(() => import('./Navigation'));
const Entry = React.lazy(() => import('./Entry'));
const Analytics = React.lazy(() => import('./Analytics'));
const CodeExamples = React.lazy(() => import('./CodeExamples'));
const Exercises = React.lazy(() => import('./ExerciseOverview'));
const Groups = React.lazy(() => import('./Group'));

const EnroledWrapper = ({ children }: Pick<AuthoriseProps, 'children'>) => (
  <Authorise
    allow={['login', 'enrolled']}
    deny={['lti']}
    layout={(children) => <TopNavLayout selected="enrolled">{children}</TopNavLayout>}
  >
    {(user) => (typeof children === 'function' ? children(user) : children)}
  </Authorise>
);

const EnroledEntry = () => {
  useSetPreferredEntry({ path: formatRoute(enroledRoute)({}) });
  return (
    <EnroledWrapper>
      <Suspense fallback={<Loading />}>
        <Entry />
      </Suspense>
    </EnroledWrapper>
  );
};

const EnroledPage = ({
  page,
  semester,
  courseUrl,
  children,
}: {
  page: Page;
  semester: SemesterId;
  courseUrl: CourseUrl;
  children: (user: UserPublic, course: CourseOption) => ReactNode;
}) => {
  useSetPreferredEntry({ path: formatRoute(enroledRoute)({}), courseUrl, semester });
  setPageParamsUiState(setPageParamsAdt.enroled({ semester, courseUrl, page }));
  return (
    <EnroledWrapper>
      {(user) => (
        <Suspense fallback={<Loading />}>
          <Navigation page={page} semester={semester} courseUrl={courseUrl}>
            {(course) => children(user, course)}
          </Navigation>
        </Suspense>
      )}
    </EnroledWrapper>
  );
};

export const enroledRouter = concatRoutes([
  pipe(
    enroledRoute.parser,
    parser.map(() => <EnroledEntry />),
  ),
  pipe(
    enroledCourseRoute.parser,
    parser.map(({ semester, courseUrl }) => (
      <EnroledWrapper>
        <Navigate to={formatRoute(enroledExercisesRoute)({ semester, courseUrl })} />
      </EnroledWrapper>
    )),
  ),
  pipe(
    enroledAnalyticsRoute.parser,
    parser.map(({ semester, courseUrl, highscoreChartSection }) => (
      <EnroledPage page="analytics" semester={semester} courseUrl={courseUrl}>
        {(_, course) =>
          course.analytics.active ? (
            <Analytics course={course} highscoreChartSection={highscoreChartSection} />
          ) : (
            <NotFound />
          )
        }
      </EnroledPage>
    )),
  ),
  pipe(
    enroledCodeExamplesRoute.parser,
    parser.map(({ semester, courseUrl }) => (
      <EnroledPage page="codeExamples" semester={semester} courseUrl={courseUrl}>
        {(user, course) =>
          course.codeExamples ? <CodeExamples userId={user._id} course={course} /> : <NotFound />
        }
      </EnroledPage>
    )),
  ),
  pipe(
    enroledExercisesRoute.parser,
    parser.map(({ semester, courseUrl }) => (
      <EnroledPage page="exercises" semester={semester} courseUrl={courseUrl}>
        {(user, course) => <Exercises userId={user._id} course={course} />}
      </EnroledPage>
    )),
  ),
  pipe(
    enroledGroupsRoute.parser,
    parser.map(({ semester, courseUrl }) => (
      <EnroledPage page="groups" semester={semester} courseUrl={courseUrl}>
        {(_, course) => <Groups semester={semester} courseId={course._id} />}
      </EnroledPage>
    )),
  ),
]);
